import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  createSetting,
  deleteSetting,
  listSettings,
  updateSetting,
} from "./settingsService";

const shouldUppercase = (field) =>
  field.uppercase !== false &&
  !["email", "url", "password", "number", "checkbox", "select"].includes(field.type);

export default function CrudSettings({
  title,
  description,
  path,
  fields,
}) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState({});

  const formFields = useMemo(
    () => fields.filter((field) => field.showInForm !== false && !field.readOnly),
    [fields]
  );
  const tableFields = useMemo(
    () => fields.filter((field) => field.showInTable !== false).slice(0, 5),
    [fields]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setItems(await listSettings(path));
    } catch (error) {
      toast.error(`Could not load ${title}.`);
    } finally {
      setLoading(false);
    }
  }, [path, title]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let active = true;
    const optionFields = fields.filter((field) => field.optionsPath);

    Promise.all(
      optionFields.map(async (field) => {
        const results = await listSettings(field.optionsPath);
        return [field.name, results];
      })
    )
      .then((entries) => {
        if (active) setDynamicOptions(Object.fromEntries(entries));
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [fields]);

  const updateValue = (field, value) => {
    const nextValue = shouldUppercase(field) && typeof value === "string"
      ? value.toUpperCase()
      : value;
    setForm((current) => ({ ...current, [field.name]: nextValue }));
  };

  const save = async (event) => {
    event.preventDefault();
    try {
      if (editId) await updateSetting(path, editId, form);
      else await createSetting(path, form);

      toast.success(`${title} saved.`);
      setForm({});
      setEditId(null);
      await load();
    } catch (error) {
      const data = error.response?.data;
      const message = data && typeof data === "object"
        ? Object.values(data).flat().join(" ")
        : "Please check the supplied values.";
      toast.error(message);
    }
  };

  const renderInput = (field) => {
    if (field.type === "checkbox") {
      return (
        <div className="settings-check-row">
          <input
            type="checkbox"
            checked={Boolean(form[field.name])}
            onChange={(event) => updateValue(field, event.target.checked)}
          />
          <small>{form[field.name] ? "YES" : "NO"}</small>
        </div>
      );
    }

    const options = field.optionsPath
      ? dynamicOptions[field.name] || []
      : field.options || [];

    if (field.type === "select" || field.optionsPath) {
      return (
        <select
          value={form[field.name] ?? ""}
          onChange={(event) => updateValue(field, event.target.value)}
          required={field.required}
        >
          <option value="">SELECT...</option>
          {options.map((option) => {
            const value = field.optionsPath
              ? option[field.optionValue || "id"]
              : option.value;
            const label = field.optionsPath
              ? option[field.optionLabel || "name"]
              : option.label;
            return <option key={value} value={value}>{String(label).toUpperCase()}</option>;
          })}
        </select>
      );
    }

    if (field.type === "textarea") {
      return (
        <textarea
          rows="3"
          value={form[field.name] ?? ""}
          onChange={(event) => updateValue(field, event.target.value)}
          required={field.required}
        />
      );
    }

    return (
      <input
        type={field.type || "text"}
        value={form[field.name] ?? ""}
        onChange={(event) => updateValue(field, event.target.value)}
        required={field.required}
        min={field.min}
        step={field.step}
      />
    );
  };

  const displayValue = (item, field) => {
    if (field.displayField) return item[field.displayField] || "—";
    const value = item[field.name];
    if (typeof value === "boolean") return value ? "YES" : "NO";
    return value ?? "—";
  };

  return (
    <section className="settings-panel">
      <div className="settings-panel__head">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        
      </div>

      <form className="settings-form-grid" onSubmit={save}>
        {formFields.map((field) => (
          <label key={field.name} className={field.wide ? "settings-wide-field" : ""}>
            <span>{field.label}</span>
            {renderInput(field)}
          </label>
        ))}

        <div className="settings-form-actions">
          <button type="submit">
            <i className="bi bi-check2-circle"></i>
            {editId ? "UPDATE RECORD" : "ADD RECORD"}
          </button>
          {editId && (
            <button type="button" className="secondary" onClick={() => { setEditId(null); setForm({}); }}>
              CANCEL
            </button>
          )}
        </div>
      </form>

      <div className="settings-table-wrap">
        <table>
          <thead>
            <tr>
              {tableFields.map((field) => <th key={field.name}>{field.label}</th>)}
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={tableFields.length + 1} className="settings-empty">LOADING RECORDS...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={tableFields.length + 1} className="settings-empty">NO RECORDS HAVE BEEN ADDED YET.</td></tr>
            ) : items.map((item) => (
              <tr key={item.id}>
                {tableFields.map((field) => <td key={field.name}>{displayValue(item, field)}</td>)}
                <td>
                  <button type="button" className="settings-icon-action" title="Edit" aria-label="Edit" onClick={() => { setEditId(item.id); setForm(item); }}><i className="bi bi-pencil-square"></i></button>
                  <button type="button" className="settings-icon-action danger" title="Delete" aria-label="Delete" onClick={async () => {
                    if (window.confirm("DELETE THIS RECORD?")) {
                      await deleteSetting(path, item.id);
                      await load();
                    }
                  }}><i className="bi bi-trash3"></i></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
